<template>
  <div
    v-if="isContainerOpen"
    class="ai-scrollbar tw:mt-3 tw:flex tw:h-[80px] tw:flex-wrap tw:gap-x-2 tw:gap-y-1 tw:overflow-y-auto tw:px-2"
  >
    <template v-if="isSuggestionVisible">
      <Transition
        appear
        name="keyword"
      >
        <div class="tw:flex tw:flex-wrap tw:gap-2">
          <AITag
            v-for="(keywordSuggestion, index) in keywordSuggestions"
            :key="index"
            :active="isSuggestionSelected(index)"
            data-testid="keyword-result"
            :label="keywordSuggestion"
            @click="toggleSuggestion(keywordSuggestion, index)"
          />
        </div>
      </Transition>
    </template>
    <div
      v-else
      class="tw:h-full tw:w-full tw:place-items-start"
    >
      <template v-if="keywordApiError">
        <BaseIcon
          class="tw:text-orange-500"
          icon="exclamation-circle"
        />

        <span
          v-if="safetyError"
          class="tw:ml-1 tw:text-orange-500"
        >
          {{
            getLocaleText(
              "suggested_keywords_ai_suggestions.safety_violation_error"
            )
          }}
        </span>

        <span
          v-else
          class="tw:ml-1 tw:text-orange-500"
        >
          {{
            getLocaleText(
              "suggested_keywords_ai_suggestions.error_loading_suggestions"
            )
          }}
        </span>
      </template>

      <template v-else-if="isLoading">
        <Transition
          appear
          name="loading"
        >
          <span class="">
            {{
              getLocaleText(
                "suggested_keywords_ai_suggestions.loading_keyword_suggestion"
              )
            }}
          </span>
        </Transition>
      </template>

      <template v-else>
        <BaseIcon
          class="tw:text-orange-500"
          icon="exclamation-circle"
        />

        <span class="tw:ml-1 tw:text-orange-500">
          {{
            getLocaleText("suggested_keywords_ai_suggestions.no_suggestions")
          }}
        </span>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getLocaleText } from "shared/boot/i18n";
import AITag from "shared/components/AI/AITag.vue";
import { BaseIcon } from "shared/components/base";
import { type SimpleBooleanFilter } from "shared/resources/Stream";

interface SuggestedKeywordsAISuggestionsProps {
  isContainerOpen?: boolean;
  keywordSuggestions?: string[];
  tags: SimpleBooleanFilter[];
  keywordApiError?: boolean;
  safetyError?: boolean;
  isLoading?: boolean;
  isSuggestionVisible?: boolean;
}

const props = defineProps<SuggestedKeywordsAISuggestionsProps>();

const emit = defineEmits<{
  removeSuggestion: [index: number];
  addSuggestion: [value: string, index: number];
}>();

function toggleSuggestion(value: string, index: number) {
  if (isSuggestionSelected(index)) {
    emit("removeSuggestion", index);
  } else {
    emit("addSuggestion", value, index);
  }
}

function isSuggestionSelected(index: number) {
  return Boolean(props.tags.find((tag) => tag.suggestionIndex === index));
}
</script>

<style lang="scss" scoped>
@import "shared/css/components/ai.scss";

.loading-appear-active,
.loading-enter-active,
.loading-leave-active {
  transition: all 0.8s ease-in-out;
  transition-delay: 1s;
  opacity: 0;
}

.loading-appear-from,
.loading-enter-from,
.loading-leave-to {
  transform: scale(0);
  opacity: 0;
}

.loading-appear-to,
.loading-enter-to,
.loading-leave-from {
  transform: scale(1);
  opacity: 1;
}

.keyword-appear-active,
.keyword-enter-active,
.keyword-leave-active {
  transition: all 0.6s ease-in-out;
  opacity: 0;
}

.keyword-appear-from,
.keyword-enter-from,
.keyword-leave-to {
  opacity: 0;
}

.keyword-appear-to,
.keyword-enter-to,
.keyword-leave-from {
  opacity: 1;
}
</style>
